/**
 * FirstRunCVModal
 *
 * Pop-up modal that appears on top of whatever page the user is on (most
 * commonly /swipe right after signup) prompting them to upload their CV.
 *
 * Trigger logic:
 *   - User is authenticated AND has zero CVs uploaded
 *   - Not dismissed yet this session
 *   - Not currently on the dedicated /onboarding/cv page (avoid duplicate UI)
 *
 * On success:
 *   - Closes the modal (no navigation) so the user is already inside the app
 *     they wanted to be in (/swipe, /jobs, /dashboard, etc.)
 *
 * This is intentionally separate from the full-page OnboardingCV component
 * which is still used by the default-trial funnel where Stripe checkout
 * is the natural next step.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Loader2, ArrowRight, ShieldCheck, FileText, X, BadgeCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { uploadCv } from '../utils/uploadCv';
import { track, EVENTS } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'jobsurf_cv_popup_dismissed';

export default function FirstRunCVModal() {
  const { isAuthenticated, token, getAuthHeaders, user } = useAuth();
  const location = useLocation();

  const [shouldShow, setShouldShow] = useState(false);
  const [stage, setStage] = useState('idle'); // idle | uploading | parsed
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedCv, setUploadedCv] = useState(null);
  const inputRef = useRef(null);

  // Decide whether to open the modal on mount / on route change.
  useEffect(() => {
    let cancelled = false;
    const decide = async () => {
      if (!isAuthenticated || !token) return;
      // The full-page onboarding handles this already — don't duplicate.
      if (location.pathname.startsWith('/onboarding')) return;
      // Founders/admins don't need this.
      if (user?.is_founder || user?.is_admin) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY) === 'true') return;
      } catch (_) {}
      try {
        const res = await axios.get(`${API}/cv/list`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const hasCv = Array.isArray(res.data) && res.data.length > 0;
        if (!hasCv) {
          setShouldShow(true);
          try { track('first_run_cv_modal_opened', { path: location.pathname }); } catch (_) {}
        }
      } catch (_) { /* swallow — never block UI on this */ }
    };
    decide();
    return () => { cancelled = true; };
  }, [isAuthenticated, token, location.pathname, getAuthHeaders, user?.is_founder, user?.is_admin]);

  const close = () => {
    setShouldShow(false);
    try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch (_) {}
  };

  const handleFile = async (file) => {
    if (!file) return;
    setStage('uploading');
    setProgress(0);
    try { track(EVENTS.CV_UPLOAD_STARTED, { source: 'first_run_modal', filename: file.name }); } catch (_) {}
    try {
      const result = await uploadCv(file, token, setProgress);
      try { track(EVENTS.CV_UPLOAD_COMPLETED, { source: 'first_run_modal' }); } catch (_) {}
      setUploadedCv(result);
      setStage('parsed');
    } catch (e) {
      toast.error(e.message || 'Upload failed. Try a different file (PDF, DOCX, or TXT).');
      setStage('idle');
      setProgress(0);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const goSwiping = () => {
    try { track('first_run_cv_modal_finished'); } catch (_) {}
    close();
    // Force a clean reload onto /swipe so pages (SwipeMode, Dashboard) refetch
    // the user's CV and unlock all CV-dependent UI without stale state.
    try {
      window.location.assign('/swipe');
    } catch (_) {
      /* fallback: just close the modal */
    }
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-3 sm:p-6"
          data-testid="first-run-cv-modal"
        >
          <motion.div
            initial={{ scale: 0.94, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: 24, opacity: 0 }}
            transition={{ type: 'spring', damping: 24, stiffness: 280 }}
            className="relative w-full max-w-lg bg-[#0E0E0E] border border-white/[0.08] rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] p-6 sm:p-8 overflow-hidden"
          >
            {/* Soft accent glows */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

            {/* Close — only when nothing in flight */}
            {stage !== 'uploading' && (
              <button
                onClick={close}
                className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors z-10"
                data-testid="first-run-cv-modal-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}

            <div className="relative">
              {stage !== 'parsed' && (
                <>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-2">
                    {user?.name ? `Welcome, ${(user.name || '').split(' ')[0]}` : 'Welcome'}
                  </p>
                  <h2 className="font-[Cabinet_Grotesk,sans-serif] text-2xl sm:text-3xl font-extrabold tracking-tight text-white leading-tight mb-2">
                    Upload your CV to start swiping.
                  </h2>
                  <p className="text-sm text-zinc-400 mb-5">
                    Drop it here and we'll match you to jobs instantly. Takes ~10 seconds.
                  </p>
                </>
              )}

              {/* Dropzone */}
              {stage !== 'parsed' && (
                <div
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => stage === 'idle' && inputRef.current?.click()}
                  className={`relative rounded-2xl border-2 border-dashed ${dragActive ? 'border-blue-400 bg-blue-500/[0.06]' : 'border-white/[0.1] bg-white/[0.02]'} ${stage === 'idle' ? 'cursor-pointer hover:border-blue-400/60' : ''} p-6 sm:p-8 text-center transition-colors`}
                  data-testid="first-run-cv-modal-dropzone"
                >
                  <input
                    type="file"
                    ref={inputRef}
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                    data-testid="first-run-cv-modal-file-input"
                  />
                  {stage === 'idle' && (
                    <>
                      <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-white/[0.06] border border-white/[0.1] flex items-center justify-center text-blue-400">
                        <Upload className="w-5 h-5" />
                      </div>
                      <p className="font-semibold text-white text-base mb-1">Drop your CV here</p>
                      <p className="text-xs text-zinc-500 mb-4">PDF, DOCX, or TXT · up to 50MB</p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                        className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm rounded-full px-5 py-2.5 hover:bg-zinc-200 transition-colors"
                        data-testid="first-run-cv-modal-browse-btn"
                      >
                        Choose file <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {stage === 'uploading' && (
                    <div className="py-2">
                      <Loader2 className="w-8 h-8 mx-auto text-blue-400 animate-spin mb-3" />
                      <p className="font-semibold text-white mb-2">Uploading your CV…</p>
                      <div className="max-w-xs mx-auto h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-400"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.2 }}
                        />
                      </div>
                      <p className="text-xs text-zinc-500 mt-2 tabular-nums">{progress}%</p>
                    </div>
                  )}
                </div>
              )}

              {/* Success */}
              {stage === 'parsed' && uploadedCv && (
                <div className="text-center py-2" data-testid="first-run-cv-modal-success">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <BadgeCheck className="w-7 h-7 text-emerald-400" strokeWidth={2.4} />
                  </div>
                  <h2 className="font-[Cabinet_Grotesk,sans-serif] text-2xl font-bold text-white mb-1">You're in.</h2>
                  <p className="text-sm text-zinc-400 mb-1 truncate" title={uploadedCv.filename}>{uploadedCv.filename}</p>
                  <p className="text-xs text-zinc-500 mb-6">Your matches are loading. Start swiping right on the ones you want.</p>
                  <button
                    onClick={goSwiping}
                    className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm rounded-full px-6 py-3.5 shadow-[0_0_40px_rgba(255,255,255,0.18)] hover:scale-[1.02] active:scale-95 transition-all"
                    data-testid="first-run-cv-modal-finish-btn"
                  >
                    Get swiping <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Trust strip */}
              {stage !== 'parsed' && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Bank-level encryption</span>
                  <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-blue-400" /> Never shared</span>
                  <span className="inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5 text-rose-400" /> Delete anytime</span>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
